import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/dates";
import { fmtMoney } from "@/lib/time";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, Download, Pencil, CheckCircle, XCircle, CreditCard, Trash2, Undo2, ExternalLink, Plus,
} from "lucide-react";
import { DateInput } from "@/components/DateInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useT } from "@/i18n";

const STATUS_BADGE = {
  Draft: "bg-slate-100 text-slate-600 border-slate-200",
  Issued: "bg-sky-50 text-sky-800 border-sky-200",
  Paid: "bg-green-50 text-green-700 border-green-200",
  Cancelled: "bg-red-50 text-red-700 border-red-200",
};

const TREATMENT_LABEL = {
  standard: "21% VAT",
  zero: "0% VAT",
  reverse_charge: "Reverse charge",
};

const PAY_STATUS_BADGE = {
  unpaid: "bg-slate-100 text-slate-600 border-slate-200",
  partial: "bg-amber-50 text-amber-800 border-amber-200",
  paid: "bg-green-50 text-green-700 border-green-200",
  overdue: "bg-red-50 text-red-700 border-red-200",
};

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm text-slate-800 mt-1 break-words">{children || "—"}</div>
    </div>
  );
}

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useT();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState("");
  const [paymentLink, setPaymentLink] = useState("");
  const [busy, setBusy] = useState(false);
  const [payData, setPayData] = useState(null);
  const [payForm, setPayForm] = useState(null);
  const [receipts, setReceipts] = useState([]);

  const load = () => {
    api.get(`/invoices/${id}`)
      .then((r) => {
        setInv(r.data);
        setPaymentLink(r.data.payment_link || "");
      })
      .catch((e) => setError(formatApiError(e)));
  };

  useEffect(load, [id]);

  const loadReceipts = () => {
    api.get("/receipts", { params: { invoice_id: id } })
      .then((r) => setReceipts(r.data))
      .catch(() => {});
  };
  useEffect(loadReceipts, [id]);

  const loadPayments = () => {
    api.get(`/invoices/${id}/payments`).then((r) => setPayData(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (inv && (inv.status === "Issued" || inv.status === "Paid")) loadPayments();
  }, [inv?.id, inv?.status]);

  if (error) {
    return (
      <div className="text-sm text-red-700" data-testid="invoice-detail-error">
        {error} — <Link to="/invoices" className="underline">{t("invoice.back")}</Link>
      </div>
    );
  }
  if (!inv) return <div className="text-sm text-slate-500" data-testid="invoice-detail-loading">{t("common.loading")}</div>;

  const act = async (action, confirmMsg, successMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      await api.post(`/invoices/${id}/${action}`);
      toast.success(successMsg);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    const isTest = inv.invoice_number.toUpperCase().startsWith("TEST");
    const confirmMsg = isTest && inv.status !== "Draft"
      ? t("invoice.delete_test_confirm", { number: inv.invoice_number, status: t(`val.${inv.status}`) })
      : t("invoice.delete_confirm");
    if (!window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      await api.delete(`/invoices/${id}`);
      toast.success(t("invoice.deleted"));
      navigate("/invoices");
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  const savePaymentLink = async () => {
    setBusy(true);
    try {
      await api.patch(`/invoices/${id}/details`, { payment_link: paymentLink });
      toast.success(t("invoice.payment_saved"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const changeLanguage = async (lang) => {
    if (lang === inv.language) return;
    setBusy(true);
    try {
      await api.patch(`/invoices/${id}/details`, { language: lang });
      toast.success(t("invoice.language_saved"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const createReceipt = async (paymentId) => {
    setBusy(true);
    try {
      await api.post(`/payments/${paymentId}/receipt`);
      toast.success(t("pay.receipt_created"));
      loadReceipts();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const receiptFor = (pid) => receipts.find((r) => r.payment_id === pid && r.status !== "Cancelled");
  const receiptPdfUrl = (rid) => `${process.env.REACT_APP_BACKEND_URL}/api/receipts/${rid}/pdf`;

  const savePayment = async (allowOverpay) => {
    setBusy(true);
    try {
      const payload = {
        payment_date: payForm.payment_date,
        amount: parseFloat(payForm.amount),
        method: payForm.method,
        note: payForm.note,
        allow_overpay: allowOverpay,
        issue_receipt: !payForm.id && !!payForm.issue_receipt,
      };
      if (payForm.id) {
        await api.put(`/payments/${payForm.id}`, payload);
        toast.success(t("pay.updated"));
      } else {
        await api.post(`/invoices/${id}/payments`, payload);
        toast.success(t("pay.added"));
        if (payload.issue_receipt) toast.success(t("pay.receipt_created"));
      }
      setPayForm(null);
      loadPayments();
      loadReceipts();
    } catch (e) {
      if (e?.response?.status === 409 && !allowOverpay) {
        if (window.confirm(t("pay.overpay_warning", { paid: payForm.amount, gross: payData?.gross }))) {
          setBusy(false);
          return savePayment(true);
        }
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const deletePayment = async (pid) => {
    if (!window.confirm(t("pay.delete_confirm"))) return;
    setBusy(true);
    try {
      await api.delete(`/payments/${pid}`);
      toast.success(t("pay.deleted"));
      loadPayments();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const timeLines = inv.lines.filter((l) => l.source_type === "time");
  const expLines = inv.lines.filter((l) => l.source_type === "expense");
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/invoices/${inv.id}/pdf`;
  const qty = (q) => {
    const s = Number(q).toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
    return s || "0";
  };

  const LineTable = ({ lines, testid }) => (
    <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <th className="px-4 py-2.5">{t("common.date")}</th>
            <th className="px-4 py-2.5">{t("common.description")}</th>
            <th className="px-4 py-2.5">{t("invoice.col_unit")}</th>
            <th className="px-4 py-2.5 text-right">{t("invoice.col_qty")}</th>
            <th className="px-4 py-2.5 text-right">{t("invoice.col_rate")}</th>
            <th className="px-4 py-2.5 text-right">{t("invoice.col_vat")}</th>
            <th className="px-4 py-2.5 text-right">{t("invoice.col_amount")}</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.source_id} className="border-t border-slate-100" data-testid={`detail-line-${l.source_type}-${l.source_id}`}>
              <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{formatDate(l.date)}</td>
              <td className="px-4 py-2.5 text-slate-800">{l.description}</td>
              <td className="px-4 py-2.5 text-slate-600">{l.unit}</td>
              <td className="px-4 py-2.5 text-right text-slate-600">{qty(l.quantity)}</td>
              <td className="px-4 py-2.5 text-right text-slate-600">{fmtMoney(l.rate)}</td>
              <td className="px-4 py-2.5 text-right text-slate-600">{qty(l.vat_rate)}%</td>
              <td className="px-4 py-2.5 text-right font-semibold text-slate-800">{fmtMoney(l.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div data-testid="invoice-detail-page" className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <Link to="/invoices" data-testid="back-to-invoices-link"
            className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 mb-2">
            <ArrowLeft className="h-3.5 w-3.5" /> {t("invoice.back")}
          </Link>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-serif text-3xl tracking-tight text-slate-900">{inv.invoice_number}</h1>
            <span data-testid="invoice-status-badge"
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${STATUS_BADGE[inv.status] || ""}`}>
              {t(`val.${inv.status}`)}
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-1">{inv.client} · {inv.matter_name}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" data-testid="download-pdf-button">
              <Download className="h-4 w-4 mr-1.5" /> {t("invoice.download_pdf")}
            </Button>
          </a>
          {inv.status === "Draft" && (
            <>
              <Button variant="outline" data-testid="edit-invoice-button" onClick={() => navigate(`/invoices/${inv.id}/edit`)}>
                <Pencil className="h-4 w-4 mr-1.5" /> {t("common.edit")}
              </Button>
              <Button data-testid="issue-invoice-button" disabled={busy}
                onClick={() => act("issue", t("invoice.issue_confirm", { number: inv.invoice_number }), t("invoice.issued"))}
                className="bg-slate-900 hover:bg-slate-800">
                <CheckCircle className="h-4 w-4 mr-1.5" /> {t("invoice.mark_issued")}
              </Button>
            </>
          )}
          {(inv.status === "Draft" || inv.invoice_number.toUpperCase().startsWith("TEST")) && (
            <Button variant="outline" data-testid="delete-invoice-button" disabled={busy} onClick={del}
              className="text-red-700 border-red-200 hover:bg-red-50">
              <Trash2 className="h-4 w-4 mr-1.5" /> {t("invoice.delete")}
            </Button>
          )}
          {inv.status === "Issued" && (
            <>
              <Button data-testid="mark-paid-button" disabled={busy}
                onClick={() => act("mark-paid", t("invoice.paid_confirm", { number: inv.invoice_number }), t("invoice.paid"))}
                className="bg-green-700 hover:bg-green-600">
                <CheckCircle className="h-4 w-4 mr-1.5" /> {t("invoice.mark_paid")}
              </Button>
              <Button variant="outline" data-testid="cancel-invoice-button" disabled={busy}
                onClick={() => act("cancel", t("invoice.cancel_confirm", { number: inv.invoice_number }), t("invoice.cancelled"))}
                className="text-red-700 border-red-200 hover:bg-red-50">
                <XCircle className="h-4 w-4 mr-1.5" /> {t("invoice.cancel_invoice")}
              </Button>
            </>
          )}
          {inv.status === "Cancelled" && !inv.entries_released && (
            <Button variant="outline" data-testid="release-entries-button" disabled={busy}
              onClick={() => act("release-entries", t("invoice.release_confirm"), t("invoice.released"))}
              className="text-slate-700">
              <Undo2 className="h-4 w-4 mr-1.5" /> {t("invoice.release")}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4 space-y-6">
          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="invoice-info-card">
            <h3 className="font-serif text-lg text-slate-800">{t("invoice.details")}</h3>
            <Field label={t("invoice.date")}>{formatDate(inv.invoice_date)}</Field>
            <Field label={t("invoice.due")}>{formatDate(inv.due_date)}</Field>
            <Field label={t("invoice.client")}>{inv.client}</Field>
            {inv.client_code && <Field label={t("invoice.client_code")}>{inv.client_code}</Field>}
            {inv.client_vat_number && <Field label={t("invoice.client_vat")}>{inv.client_vat_number}</Field>}
            {inv.client_address && <Field label={t("invoice.client_address")}>{inv.client_address}</Field>}
            <Field label={t("invoice.matter_label")}>
              <Link to={`/matters/${inv.matter_id}`} className="hover:underline" data-testid="invoice-matter-link">
                {inv.matter_name}
              </Link>
            </Field>
            <Field label={t("invoice.language")}>
              {inv.status === "Draft" || inv.status === "Issued" ? (
                <Select value={inv.language} onValueChange={changeLanguage} disabled={busy}>
                  <SelectTrigger data-testid="invoice-detail-language-select" className="h-8 w-40 mt-0.5 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LT" data-testid="invoice-detail-language-lt">{t("invoice.lang_lt")}</SelectItem>
                    <SelectItem value="EN" data-testid="invoice-detail-language-en">{t("invoice.lang_en")}</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                inv.language === "LT" ? t("invoice.lang_lt") : t("invoice.lang_en")
              )}
            </Field>
            <Field label={t("invoice.currency")}>{inv.currency}</Field>
            <Field label={t("invoice.vat_treatment")}>{t(`val.${inv.vat_treatment}`)}</Field>
            {inv.vat_note && <Field label={t("invoice.vat_note")}>{inv.vat_note}</Field>}
            {inv.internal_notes && <Field label={t("invoice.internal_notes")}>{inv.internal_notes}</Field>}
            {inv.entries_released && (
              <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                {t("invoice.released_note")}
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="payment-link-card">
            <h3 className="font-serif text-lg text-slate-800 mb-3">{t("invoice.payment_link_title")}</h3>
            {inv.status === "Draft" || inv.status === "Issued" ? (
              <div className="flex gap-2">
                <Input
                  data-testid="payment-link-input"
                  value={paymentLink}
                  onChange={(e) => setPaymentLink(e.target.value)}
                  placeholder="https://…"
                  className="h-9 text-sm"
                />
                <Button size="sm" data-testid="payment-link-save" onClick={savePaymentLink} disabled={busy}
                  className="bg-slate-900 hover:bg-slate-800 shrink-0">
                  {t("common.save")}
                </Button>
              </div>
            ) : null}
            {inv.payment_link ? (
              <a href={inv.payment_link} target="_blank" rel="noopener noreferrer" data-testid="payment-link-open"
                className="mt-3 inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-slate-900 text-sm font-bold px-4 py-2 rounded-md transition-colors">
                <CreditCard className="h-4 w-4" />
                {inv.language === "LT" ? "Apmokėti sąskaitą" : "Pay invoice"}
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            ) : (
              <p className="text-xs text-slate-400 mt-2">{t("invoice.no_payment_link")}</p>
            )}
          </div>

          {(inv.status === "Issued" || inv.status === "Paid") && payData && (
            <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="payments-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-serif text-lg text-slate-800">{t("pay.title")}</h3>
                <span data-testid="payment-status-badge"
                  className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${PAY_STATUS_BADGE[payData.payment_status] || ""}`}>
                  {t(`pay.status.${payData.payment_status}`)}
                </span>
              </div>
              <div className="space-y-1.5 text-sm mb-4">
                <div className="flex justify-between">
                  <span className="text-slate-500">{t("invoice.totals_total")}</span>
                  <span className="font-semibold" data-testid="pay-gross">{fmtMoney(payData.gross)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">{t("pay.total_paid")}</span>
                  <span className="font-semibold text-green-700" data-testid="pay-total-paid">{fmtMoney(payData.total_paid)}</span>
                </div>
                <div className="flex justify-between border-t border-slate-200 pt-1.5">
                  <span className="font-semibold text-slate-700">{t("pay.outstanding")}</span>
                  <span className="font-bold" data-testid="pay-outstanding">{fmtMoney(payData.outstanding)}</span>
                </div>
                {payData.days_overdue > 0 && (
                  <div className="text-xs text-red-700 font-semibold" data-testid="pay-days-overdue">
                    {t("pay.days_overdue")}: {payData.days_overdue}
                  </div>
                )}
              </div>
              {payData.payments.length === 0 ? (
                <p className="text-xs text-slate-400 mb-3" data-testid="payments-empty">{t("pay.empty")}</p>
              ) : (
                <div className="space-y-2 mb-3" data-testid="payments-list">
                  {payData.payments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between gap-2 text-sm border border-slate-100 rounded-md px-3 py-2" data-testid={`payment-row-${p.id}`}>
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-800">
                          {fmtMoney(p.amount)} <span className="font-normal text-slate-500">· {formatDate(p.payment_date)}</span>
                        </div>
                        {(p.method || p.note) && (
                          <div className="text-xs text-slate-500 truncate">
                            {["bank", "cash", "other"].includes(p.method) ? t(`pay.method_${p.method}`) : p.method}
                            {p.method && p.note ? " · " : ""}{p.note}
                          </div>
                        )}
                        {p.method === "cash" && (() => {
                          const rc = receiptFor(p.id);
                          return rc ? (
                            <div className="text-xs mt-0.5" data-testid={`payment-receipt-${p.id}`}>
                              {rc.status === "Draft" ? (
                                <a href="/receipts" className="text-slate-500 underline hover:text-slate-800">
                                  {t("pay.receipt")}: {t("val.Draft")}
                                </a>
                              ) : (
                                <a href={receiptPdfUrl(rc.id)} target="_blank" rel="noreferrer"
                                  className="text-slate-700 underline hover:text-slate-900">
                                  {t("pay.receipt")} {rc.receipt_number}
                                </a>
                              )}
                            </div>
                          ) : (
                            <button data-testid={`payment-create-receipt-${p.id}`} disabled={busy}
                              onClick={() => createReceipt(p.id)}
                              className="text-xs text-slate-500 underline hover:text-slate-800 mt-0.5 transition-colors">
                              {t("pay.create_receipt")}
                            </button>
                          );
                        })()}
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <button data-testid={`payment-edit-${p.id}`}
                          onClick={() => setPayForm({ id: p.id, payment_date: p.payment_date, amount: String(p.amount), method: p.method, note: p.note })}
                          className="p-1.5 text-slate-400 hover:text-slate-700 transition-colors">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button data-testid={`payment-delete-${p.id}`} onClick={() => deletePayment(p.id)}
                          className="p-1.5 text-slate-400 hover:text-red-700 transition-colors">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {payForm ? (
                <div className="border border-slate-200 rounded-md p-3 space-y-2" data-testid="payment-form">
                  <DateInput testid="payment-date-input" value={payForm.payment_date}
                    onChange={(v) => setPayForm({ ...payForm, payment_date: v || "" })} />
                  <Input data-testid="payment-amount-input" type="number" step="0.01" min="0" placeholder={t("pay.amount")}
                    value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className="h-9 text-sm" />
                  <Select value={payForm.method || "bank"}
                    onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                    <SelectTrigger data-testid="payment-method-select" className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank" data-testid="payment-method-bank">{t("pay.method_bank")}</SelectItem>
                      <SelectItem value="cash" data-testid="payment-method-cash">{t("pay.method_cash")}</SelectItem>
                      <SelectItem value="other" data-testid="payment-method-other">{t("pay.method_other")}</SelectItem>
                    </SelectContent>
                  </Select>
                  {payForm.method === "cash" && !payForm.id && (
                    <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer"
                      data-testid="issue-receipt-label">
                      <input type="checkbox" data-testid="issue-receipt-checkbox" checked={!!payForm.issue_receipt}
                        onChange={(e) => setPayForm({ ...payForm, issue_receipt: e.target.checked })} />
                      {t("pay.issue_receipt")}
                    </label>
                  )}
                  <Input data-testid="payment-note-input" placeholder={t("pay.note")}
                    value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} className="h-9 text-sm" />
                  <div className="flex gap-2">
                    <Button size="sm" data-testid="payment-save-button" disabled={busy} onClick={() => savePayment(false)}
                      className="bg-slate-900 hover:bg-slate-800">{t("pay.save")}</Button>
                    <Button size="sm" variant="outline" data-testid="payment-cancel-button" onClick={() => setPayForm(null)}>
                      {t("common.cancel")}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button size="sm" variant="outline" data-testid="add-payment-button"
                  onClick={() => setPayForm({ payment_date: new Date().toISOString().slice(0, 10), amount: "", method: "bank", note: "", issue_receipt: false })}>
                  <Plus className="h-4 w-4 mr-1.5" /> {t("pay.add")}
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="lg:col-span-8 space-y-6">
          <section data-testid="invoice-time-lines">
            <h2 className="font-serif text-xl text-slate-800 mb-3">{t("invoice.services")} ({timeLines.length})</h2>
            {timeLines.length === 0
              ? <div className="text-sm text-slate-500">{t("invoice.no_time_lines")}</div>
              : <LineTable lines={timeLines} testid="invoice-time-lines-table" />}
          </section>

          <section data-testid="invoice-expense-lines">
            <h2 className="font-serif text-xl text-slate-800 mb-3">{t("invoice.reimb")} ({expLines.length})</h2>
            {expLines.length === 0
              ? <div className="text-sm text-slate-500">{t("invoice.no_expense_lines")}</div>
              : <LineTable lines={expLines} testid="invoice-expense-lines-table" />}
          </section>

          <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="invoice-totals-card">
            <div className="space-y-2 text-sm max-w-xs ml-auto">
              <div className="flex justify-between">
                <span className="text-slate-500">{t("invoice.totals_fees")}</span>
                <span className="font-semibold" data-testid="total-fees">{fmtMoney(inv.totals.fees_subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">{t("invoice.totals_vat")}</span>
                <span className="font-semibold" data-testid="total-vat">{fmtMoney(inv.totals.vat_total)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">{t("invoice.totals_expenses")}</span>
                <span className="font-semibold" data-testid="total-expenses">{fmtMoney(inv.totals.expenses_subtotal)}</span>
              </div>
              <div className="flex justify-between border-t border-slate-200 pt-2">
                <span className="font-semibold text-slate-800">{t("invoice.totals_total")}</span>
                <span className="font-bold text-slate-900 text-base" data-testid="total-payable">{fmtMoney(inv.totals.total)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
